import React, { useEffect, useState, useCallback } from "react";
import {
  fetchAllUsers, createUser, updateUserRole, updateUserStatus, updatePortalAccess,
} from "../../../services/userService";
import { ManagedUser, CreateUserPayload, PortalAccess } from "../../../types/user";
import { useAuth } from "../../../context/useAuth";
import PageHeader from "../../shared/PageHeader/PageHeader";
import Badge from "../../shared/Badge/Badge";
import "./UsersPage.css";

const LIMIT = 50;

const PORTAL_LABELS: Record<PortalAccess, string> = {
  find_your_ohio: "Find Your Ohio",
  ai_ready_ohio:  "AI Ready Ohio",
  all:            "All Portals",
};

export default function UsersPage() {
  const { email: currentUserEmail } = useAuth();

  const [users,   setUsers]   = useState<ManagedUser[]>([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create user form
  const [showCreate,   setShowCreate]   = useState(false);
  const [createEmail,  setCreateEmail]  = useState("");
  const [createFirst,  setCreateFirst]  = useState("");
  const [createLast,   setCreateLast]   = useState("");
  const [createRole,   setCreateRole]   = useState<"admin" | "reviewer">("reviewer");
  const [createPortal, setCreatePortal] = useState<PortalAccess>("find_your_ohio");
  const [createSaving, setCreateSaving] = useState(false);
  const [createError,  setCreateError]  = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchAllUsers({ limit: LIMIT, offset })
      .then((r) => { setUsers(r.users); setTotal(r.total); })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [offset]);

  useEffect(() => { load(); }, [load]);

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleCreate = async () => {
    if (!createEmail || !createFirst || !createLast) {
      setCreateError("All fields are required.");
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createUser({
        email:           createEmail.trim(),
        user_first_name: createFirst.trim(),
        user_last_name:  createLast.trim(),
        role:            createRole,
        portal_access:   createPortal,
      });
      setShowCreate(false);
      setCreateEmail(""); setCreateFirst(""); setCreateLast("");
      setCreateRole("reviewer"); setCreatePortal("find_your_ohio");
      flash("User created.");
      load();
    } catch (e: any) {
      setCreateError(e.message);
    } finally {
      setCreateSaving(false);
    }
  };

  const handleRoleChange = async (user: ManagedUser, role: "admin" | "reviewer") => {
    try {
      await updateUserRole(user.email, role, currentUserEmail);
      flash(`Role updated for ${user.email}.`);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleStatusToggle = async (user: ManagedUser) => {
    try {
      await updateUserStatus(user.email, !user.is_active, currentUserEmail);
      flash(`${user.email} ${!user.is_active ? "activated" : "deactivated"}.`);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handlePortalChange = async (user: ManagedUser, portal: PortalAccess) => {
    try {
      await updatePortalAccess(user.email, portal, currentUserEmail);
      flash(`Portal access updated for ${user.email}.`);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="fyo-page">
      <PageHeader
        title="Users"
        subtitle="Manage platform access"
        right={
          <button className="fyo-btn fyo-btn--primary" onClick={() => { setShowCreate(true); setCreateError(null); }}>
            + Add User
          </button>
        }
      />

      {error   && <div className="fyo-error-banner">{error}</div>}
      {success && <div className="fyo-success-banner">{success}</div>}

      {/* Create user form */}
      {showCreate && (
        <div className="fyo-card">
          <h2 className="fyo-section-title">New User</h2>
          <div className="fyo-users__create-form">
            <input className="fyo-input" placeholder="Email" value={createEmail}
              onChange={(e) => setCreateEmail(e.target.value)} />
            <input className="fyo-input" placeholder="First name" value={createFirst}
              onChange={(e) => setCreateFirst(e.target.value)} />
            <input className="fyo-input" placeholder="Last name" value={createLast}
              onChange={(e) => setCreateLast(e.target.value)} />
            <select className="fyo-select" value={createRole}
              onChange={(e) => setCreateRole(e.target.value as any)}>
              <option value="reviewer">Reviewer</option>
              <option value="admin">Admin</option>
            </select>
            <select className="fyo-select" value={createPortal}
              onChange={(e) => setCreatePortal(e.target.value as PortalAccess)}>
              <option value="find_your_ohio">Find Your Ohio</option>
              <option value="ai_ready_ohio">AI Ready Ohio</option>
              <option value="all">All Portals</option>
            </select>
          </div>
          {createError && <div className="fyo-error-banner" style={{ marginTop: "var(--space-3)" }}>{createError}</div>}
          <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-4)" }}>
            <button className="fyo-btn fyo-btn--primary" disabled={createSaving} onClick={handleCreate}>
              {createSaving ? "Saving..." : "Create User"}
            </button>
            <button className="fyo-btn fyo-btn--ghost"
              onClick={() => { setShowCreate(false); setCreateError(null); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="fyo-card fyo-card--no-pad">
        {loading ? (
          <p className="state-text state-text--muted" style={{ padding: "var(--space-6)" }}>Loading...</p>
        ) : (
          <>
            <table className="fyo-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Portal Access</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan={6} className="fyo-muted" style={{ textAlign: "center", padding: "var(--space-6)" }}>No users found.</td></tr>
                ) : users.map((u, i) => (
                  <tr key={u.email} className={i % 2 === 1 ? "fyo-table__row--odd" : ""}>
                    <td className="fyo-table__cell--primary">
                      {u.user_first_name} {u.user_last_name}
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <select className="fyo-select fyo-select--sm" value={u.role}
                        onChange={(e) => handleRoleChange(u, e.target.value as any)}
                        disabled={u.email === currentUserEmail}>
                        <option value="reviewer">Reviewer</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>
                      <select className="fyo-select fyo-select--sm" value={u.portal_access}
                        onChange={(e) => handlePortalChange(u, e.target.value as PortalAccess)}>
                        <option value="find_your_ohio">Find Your Ohio</option>
                        <option value="ai_ready_ohio">AI Ready Ohio</option>
                        <option value="all">All Portals</option>
                      </select>
                    </td>
                    <td>
                      <Badge text={u.is_active ? "Active" : "Do Not Use"} />
                    </td>
                    <td>
                      <button
                        className={`fyo-btn fyo-btn--sm ${u.is_active ? "fyo-btn--ghost" : "fyo-btn--primary"}`}
                        disabled={u.email === currentUserEmail}
                        onClick={() => handleStatusToggle(u)}>
                        {u.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="fyo-pagination">
              <span className="fyo-pagination__info">
                {total === 0 ? "No users" : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total}`}
              </span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset === 0}
                onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}>Prev</button>
              <span className="fyo-muted">Page {Math.floor(offset / LIMIT) + 1} of {Math.ceil(total / LIMIT) || 1}</span>
              <button className="fyo-btn fyo-btn--ghost fyo-btn--sm" disabled={offset + LIMIT >= total}
                onClick={() => setOffset((o) => o + LIMIT)}>Next</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
